export type AiLocale = "en-US" | "ja-JP" | "ko-KR";

export interface AiUiStrings {
  aiDisclaimer: string;
  applyRevisionAction: string;
  broadAgreementLabel: string;
  contestedButLegibleLabel: string;
  counterpointsLabel: string;
  evaluateArgumentAction: string;
  evaluateArgumentLoading: string;
  evaluationTitle: string;
  feedbackNegative: string;
  feedbackPositive: string;
  feedbackThanks: string;
  findingClaimsLoading: string;
  highlyContestedLabel: string;
  insufficientAwards: string;
  moderateStrengthLabel: string;
  noMoreSuggestions: string;
  noRevisionAvailable: string;
  possibleFallaciesLabel: string;
  regenerateClaimsAction: string;
  regeneratingClaimsLoading: string;
  possibleWeakPointsLabel: string;
  searchSuggestionsError: string;
  sourceLabel: string;
  strengthLabel: string;
  strongStrengthLabel: string;
  suggestClaimsAction: string;
  suggestClaimsSubtitle: string;
  suggestClaimsTitle: string;
  suggestedRevisionLabel: string;
  termsNeedingDefinitionLabel: string;
  thisCostsAwards: (count: number) => string;
  useClaimAction: string;
  weakStrengthLabel: string;
  analyzeGraph: AnalyzeGraphStrings;
}

export interface AnalyzeGraphStrings {
  closeLabel: string;
  analyzeAction: string;
  reanalyzeAction: string;
  analyzingLabel: string;
  panelTitle: string;
  panelSubtitle: string;
  sectionSummary: string;
  sectionInsights: string;
  sectionRootCauses: string;
  sectionMissingLinks: string;
  emptyState: string;
  notEnoughNodes: string;
  errorState: string;
  truncatedBanner: (count: number) => string;
  missingLinkGroupBridge: string;
  missingLinkGroupDownstream: string;
  missingLinkGroupUpstream: string;
  missingLinkGroupLever: string;
  addToGraphAction: string;
  addingLabel: string;
  dismissAction: string;
  directionInfluences: string;
  directionInfluencedBy: string;
  anchorLabel: string;
  confidenceLow: string;
  confidenceMedium: string;
  confidenceHigh: string;
  wikidataChip: (qid: string) => string;
  acceptToast: string;
  acceptToastReused: string;
  acceptError: string;
  undoAcceptAction: string;
  announceReady: (insights: number, suggestions: number) => string;
  insightKindCluster: string;
  insightKindFeedbackLoop: string;
  insightKindChain: string;
  insightKindIsolate: string;
  insightKindBridgeGap: string;
  noSuggestions: string;
  noRootCauses: string;
  noInsights: string;
}

interface LocaleStringBundle extends Omit<AiUiStrings, "thisCostsAwards" | "analyzeGraph"> {
  thisCostsAwards: string;
  analyzeGraph: Omit<AnalyzeGraphStrings, "truncatedBanner" | "wikidataChip" | "announceReady"> & {
    truncatedBanner: string;
    wikidataChip: string;
    announceReady: string;
  };
}

const STRINGS: Record<AiLocale, LocaleStringBundle> = {
  "en-US": {
    aiDisclaimer: "AI suggestions are not definitive. Use your judgment before posting.",
    applyRevisionAction: "Apply revision",
    broadAgreementLabel: "Broad agreement",
    contestedButLegibleLabel: "Debate-ready",
    counterpointsLabel: "Possible counterpoints",
    evaluateArgumentAction: "Evaluate",
    evaluateArgumentLoading: "Analyzing your argument...",
    evaluationTitle: "AI evaluation",
    feedbackNegative: "Not helpful",
    feedbackPositive: "Helpful",
    feedbackThanks: "Thanks for the feedback.",
    findingClaimsLoading: "Finding debate-ready claims...",
    highlyContestedLabel: "Highly contested",
    insufficientAwards: "You do not have enough awards for this AI feature.",
    moderateStrengthLabel: "Moderate",
    noMoreSuggestions: "Couldn't find more distinct claims for this issue. Try tweaking the issue or coming back later.",
    noRevisionAvailable: "Couldn't suggest anything for the provided argument.",
    possibleFallaciesLabel: "Possible fallacies",
    regenerateClaimsAction: "Suggest different claims",
    regeneratingClaimsLoading: "Finding different claims...",
    possibleWeakPointsLabel: "Potential weak points",
    searchSuggestionsError: "Unable to generate AI suggestions right now.",
    sourceLabel: "Sources",
    strengthLabel: "Strength",
    strongStrengthLabel: "Strong",
    suggestClaimsAction: "Suggest",
    suggestClaimsSubtitle: "Get three debate-ready claim ideas based on this issue.",
    suggestClaimsTitle: "Need a starting claim?",
    suggestedRevisionLabel: "Suggested revision",
    termsNeedingDefinitionLabel: "Terms to define",
    thisCostsAwards: "This costs {count} award(s).",
    useClaimAction: "Use this claim",
    weakStrengthLabel: "Weak",
    analyzeGraph: {
      closeLabel: "Close analysis",
      analyzeAction: "Analyze graph",
      reanalyzeAction: "Re-analyze",
      analyzingLabel: "Analyzing your graph...",
      panelTitle: "Graph analysis",
      panelSubtitle: "Structural patterns, root-cause candidates, and suggested missing links grounded in Wikipedia.",
      sectionSummary: "Summary",
      sectionInsights: "Structural insights",
      sectionRootCauses: "Root cause candidates",
      sectionMissingLinks: "Suggested missing links",
      emptyState: "No results yet.",
      notEnoughNodes: "Add at least two nodes and one influence before running analysis.",
      errorState: "Could not analyze this graph.",
      truncatedBanner: "This graph is large. {count} node(s) or edge(s) were trimmed for analysis.",
      missingLinkGroupBridge: "Bridges between clusters",
      missingLinkGroupDownstream: "Downstream effects",
      missingLinkGroupUpstream: "Upstream drivers",
      missingLinkGroupLever: "Mechanisms and levers",
      addToGraphAction: "Add to graph",
      addingLabel: "Adding...",
      dismissAction: "Dismiss",
      directionInfluences: "influences",
      directionInfluencedBy: "is influenced by",
      anchorLabel: "Anchor",
      confidenceLow: "Low confidence",
      confidenceMedium: "Medium confidence",
      confidenceHigh: "High confidence",
      wikidataChip: "Wikidata {qid}",
      acceptToast: "Added to your graph.",
      acceptToastReused: "Linked an existing issue to your graph.",
      acceptError: "Could not add this suggestion.",
      undoAcceptAction: "Undo",
      announceReady: "Analysis ready: {insights} insight(s), {suggestions} suggestion(s).",
      insightKindCluster: "Cluster",
      insightKindFeedbackLoop: "Feedback loop",
      insightKindChain: "Chain",
      insightKindIsolate: "Isolated node",
      insightKindBridgeGap: "Bridge gap",
      noSuggestions: "No new missing-link suggestions right now.",
      noRootCauses: "No clear root-cause candidates yet.",
      noInsights: "No structural patterns detected yet.",
    },
  },
  "ja-JP": {
    aiDisclaimer: "AI の提案は決定版ではありません。投稿する前にご自身で判断してください。",
    applyRevisionAction: "修正文を適用",
    broadAgreementLabel: "広く共有されている論点",
    contestedButLegibleLabel: "議論を始めやすい主張",
    counterpointsLabel: "想定される反論",
    evaluateArgumentAction: "AI で評価",
    evaluateArgumentLoading: "議論を分析しています...",
    evaluationTitle: "AI 評価",
    feedbackNegative: "役に立たなかった",
    feedbackPositive: "役に立った",
    feedbackThanks: "フィードバックありがとうございます。",
    findingClaimsLoading: "議論を始めやすい主張を探しています...",
    highlyContestedLabel: "争点が大きい",
    insufficientAwards: "この AI 機能を使うためのアワードが不足しています。",
    moderateStrengthLabel: "中程度",
    noMoreSuggestions: "この Issue に対して、これ以上異なる主張を見つけられませんでした。Issue を調整するか、しばらくしてから再度お試しください。",
    noRevisionAvailable: "提示された主張に対して、AI から提案できるものはありませんでした。",
    possibleFallaciesLabel: "考えられる誤謬",
    regenerateClaimsAction: "別の主張を提案",
    regeneratingClaimsLoading: "別の主張を探しています...",
    possibleWeakPointsLabel: "弱く見える点",
    searchSuggestionsError: "今は AI 提案を生成できませんでした。",
    sourceLabel: "情報源",
    strengthLabel: "強さ",
    strongStrengthLabel: "強い",
    suggestClaimsAction: "主張を提案",
    suggestClaimsSubtitle: "この Issue に基づいて、議論を始めやすい主張を 3 つ提案します。",
    suggestClaimsTitle: "出発点となる主張が必要ですか？",
    suggestedRevisionLabel: "修正案",
    termsNeedingDefinitionLabel: "定義が必要な用語",
    thisCostsAwards: "この機能には {count} アワードが必要です。",
    useClaimAction: "この主張を使う",
    weakStrengthLabel: "弱い",
    analyzeGraph: {
      closeLabel: "分析を閉じる",
      analyzeAction: "グラフを分析",
      reanalyzeAction: "再分析",
      analyzingLabel: "グラフを分析しています...",
      panelTitle: "グラフ分析",
      panelSubtitle: "Wikipedia を基にした構造的なパターン、根本原因候補、欠けているつながりの候補。",
      sectionSummary: "要約",
      sectionInsights: "構造的な示唆",
      sectionRootCauses: "根本原因の候補",
      sectionMissingLinks: "欠けているつながりの候補",
      emptyState: "結果はまだありません。",
      notEnoughNodes: "分析を実行するには、最低 2 つのノードと 1 つの影響を追加してください。",
      errorState: "このグラフを分析できませんでした。",
      truncatedBanner: "グラフが大きいため、分析では {count} 件のノードまたはエッジが省かれました。",
      missingLinkGroupBridge: "クラスタ間のブリッジ",
      missingLinkGroupDownstream: "下流への影響",
      missingLinkGroupUpstream: "上流の要因",
      missingLinkGroupLever: "メカニズム・レバー",
      addToGraphAction: "グラフに追加",
      addingLabel: "追加中...",
      dismissAction: "閉じる",
      directionInfluences: "に影響する",
      directionInfluencedBy: "の影響を受ける",
      anchorLabel: "起点ノード",
      confidenceLow: "信頼度: 低",
      confidenceMedium: "信頼度: 中",
      confidenceHigh: "信頼度: 高",
      wikidataChip: "Wikidata {qid}",
      acceptToast: "グラフに追加しました。",
      acceptToastReused: "既存の Issue をグラフに接続しました。",
      acceptError: "この提案を追加できませんでした。",
      undoAcceptAction: "元に戻す",
      announceReady: "分析完了: 示唆 {insights} 件 / 候補 {suggestions} 件。",
      insightKindCluster: "クラスタ",
      insightKindFeedbackLoop: "フィードバックループ",
      insightKindChain: "連鎖",
      insightKindIsolate: "孤立ノード",
      insightKindBridgeGap: "クラスタ間の断絶",
      noSuggestions: "今は新しい欠けているつながりの候補はありません。",
      noRootCauses: "明確な根本原因の候補はまだありません。",
      noInsights: "明確な構造的パターンは見つかりませんでした。",
    },
  },
  "ko-KR": {
    aiDisclaimer: "AI 제안은 확정적인 답이 아닙니다. 게시하기 전에 직접 판단해 주세요.",
    applyRevisionAction: "수정안 적용",
    broadAgreementLabel: "넓게 공유되는 주장",
    contestedButLegibleLabel: "토론을 시작하기 좋은 주장",
    counterpointsLabel: "가능한 반론",
    evaluateArgumentAction: "AI 평가",
    evaluateArgumentLoading: "주장을 분석하는 중...",
    evaluationTitle: "AI 평가",
    feedbackNegative: "도움이 안 됨",
    feedbackPositive: "도움이 됨",
    feedbackThanks: "피드백 감사합니다.",
    findingClaimsLoading: "토론을 시작하기 좋은 주장을 찾는 중...",
    highlyContestedLabel: "논쟁이 큰 주장",
    insufficientAwards: "이 AI 기능을 사용하기 위한 어워드가 부족합니다.",
    moderateStrengthLabel: "보통",
    noMoreSuggestions: "이 이슈에 대해 더 다른 주장을 찾을 수 없었습니다. 이슈를 조금 바꾸거나 잠시 후 다시 시도해 주세요.",
    noRevisionAvailable: "제공된 주장에 대해 AI가 제안할 내용을 찾지 못했습니다.",
    possibleFallaciesLabel: "가능한 오류",
    regenerateClaimsAction: "다른 주장 제안 받기",
    regeneratingClaimsLoading: "다른 주장을 찾는 중...",
    possibleWeakPointsLabel: "약해 보일 수 있는 부분",
    searchSuggestionsError: "지금은 AI 제안을 만들 수 없습니다.",
    sourceLabel: "출처",
    strengthLabel: "강도",
    strongStrengthLabel: "강함",
    suggestClaimsAction: "주장 제안 받기",
    suggestClaimsSubtitle: "이 이슈를 바탕으로 토론을 시작하기 좋은 주장 3개를 제안합니다.",
    suggestClaimsTitle: "시작할 주장이 필요하신가요?",
    suggestedRevisionLabel: "수정 제안",
    termsNeedingDefinitionLabel: "정의가 필요한 표현",
    thisCostsAwards: "이 기능에는 어워드 {count}개가 필요합니다.",
    useClaimAction: "이 주장 사용",
    weakStrengthLabel: "약함",
    analyzeGraph: {
      closeLabel: "분석 닫기",
      analyzeAction: "그래프 분석",
      reanalyzeAction: "다시 분석",
      analyzingLabel: "그래프를 분석하는 중...",
      panelTitle: "그래프 분석",
      panelSubtitle: "Wikipedia를 기반으로 한 구조적 패턴, 원인 후보, 빠져 있는 연결 제안입니다.",
      sectionSummary: "요약",
      sectionInsights: "구조적 관찰",
      sectionRootCauses: "원인 후보",
      sectionMissingLinks: "빠져 있는 연결 제안",
      emptyState: "아직 결과가 없습니다.",
      notEnoughNodes: "분석을 실행하려면 최소 두 개의 노드와 하나의 영향 관계가 필요합니다.",
      errorState: "이 그래프를 분석할 수 없습니다.",
      truncatedBanner: "그래프가 커서 분석에서 {count}개의 노드 또는 엣지가 제외되었습니다.",
      missingLinkGroupBridge: "클러스터 간 다리",
      missingLinkGroupDownstream: "하류 효과",
      missingLinkGroupUpstream: "상류 동인",
      missingLinkGroupLever: "메커니즘과 지렛대",
      addToGraphAction: "그래프에 추가",
      addingLabel: "추가하는 중...",
      dismissAction: "닫기",
      directionInfluences: "영향을 줍니다",
      directionInfluencedBy: "영향을 받습니다",
      anchorLabel: "기준 노드",
      confidenceLow: "신뢰도 낮음",
      confidenceMedium: "신뢰도 보통",
      confidenceHigh: "신뢰도 높음",
      wikidataChip: "Wikidata {qid}",
      acceptToast: "그래프에 추가했습니다.",
      acceptToastReused: "기존 이슈를 그래프에 연결했습니다.",
      acceptError: "이 제안을 추가할 수 없습니다.",
      undoAcceptAction: "실행 취소",
      announceReady: "분석 완료: 관찰 {insights}건, 제안 {suggestions}건.",
      insightKindCluster: "클러스터",
      insightKindFeedbackLoop: "피드백 루프",
      insightKindChain: "연쇄",
      insightKindIsolate: "고립 노드",
      insightKindBridgeGap: "클러스터 간 단절",
      noSuggestions: "지금은 빠져 있는 연결 제안이 없습니다.",
      noRootCauses: "명확한 원인 후보가 아직 없습니다.",
      noInsights: "뚜렷한 구조 패턴이 발견되지 않았습니다.",
    },
  },
};

export function resolveAiLocale(): AiLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const language = navigator.language.toLowerCase();
  if (language.startsWith("ja")) {
    return "ja-JP";
  }
  if (language.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getAiUiStrings(locale?: AiLocale): AiUiStrings {
  const key = locale ?? resolveAiLocale();
  const selected = STRINGS[key] ?? STRINGS["en-US"];
  return {
    ...selected,
    thisCostsAwards: (count: number) => selected.thisCostsAwards.replace("{count}", String(count)),
    analyzeGraph: {
      ...selected.analyzeGraph,
      announceReady: (insights: number, suggestions: number) =>
        selected.analyzeGraph.announceReady
          .replace("{insights}", String(insights))
          .replace("{suggestions}", String(suggestions)),
      truncatedBanner: (count: number) =>
        selected.analyzeGraph.truncatedBanner.replace("{count}", String(count)),
      wikidataChip: (qid: string) =>
        selected.analyzeGraph.wikidataChip.replace("{qid}", qid),
    },
  };
}
